import React, { useEffect, useState } from "react";
interface OTPInputProps {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  error?: boolean;
}
export const OTPInput = ({ 
  value, 
  onChange, 
  disabled = false, 
  error = false 
}: OTPInputProps) => {
  const [isFocused, setIsFocused] = useState(false);

  // Handle auto-retrieval of OTP (if available)
  useEffect(() => {
    if ('OTPCredential' in window) {
      const abortController = new AbortController();

      navigator.credentials.get({
        // @ts-expect-error - OTP API is experimental and not yet in TypeScript definitions
        otp: { transport: ['sms'] },
        signal: abortController.signal
      }).then((otp) => {
        if (otp && typeof otp === 'object' && 'code' in otp) {
          const code = (otp as { code: string }).code.replace(/\D/g, '').slice(0, 6);
          if (code.length === 6) {
            onChange(code);
          }
        }
      }).catch((err) => {
        console.log('OTP auto-retrieval not available:', err);
      });

      return () => abortController.abort();
    }
  }, [onChange]);

  const handleChange = (inputValue: string) => {
    if (disabled) return;
    const numeric = inputValue.replace(/\D/g, '').slice(0, 6);
    onChange(numeric);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (disabled) return;
    const allowed = /\d/.test(e.key) || ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab', 'Enter'].includes(e.key);
    if (!allowed) {
      e.preventDefault();
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    if (disabled) return;
    const pasted = e.clipboardData.getData('text');
    const numeric = pasted.replace(/\D/g, '').slice(0, 6);
    onChange(numeric);
  };

  const hasValue = value.length > 0;

  return (
    <div className="space-y-4">
      <label className="block text-sm font-medium text-gray-700 text-center">
        Enter 6-digit verification code
      </label>

      <div className="flex justify-center">
        <input
          type="text"
          inputMode="numeric"
          pattern="\d*"
          maxLength={6}
          value={value}
          onChange={(e) => handleChange(e.target.value)}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          disabled={disabled}
          className={`
            w-64 h-14 text-center tracking-[0.6em] text-xl font-bold rounded-lg border-2
            transition-all duration-200 outline-none px-3
            ${disabled
              ? 'bg-gray-100 border-gray-200 text-gray-400 cursor-not-allowed'
              : error
                ? 'border-red-500 bg-red-50 text-red-700 focus:border-red-600 focus:ring-2 focus:ring-red-200'
                : hasValue
                  ? 'border-green-500 bg-green-50 text-green-700 focus:border-green-600 focus:ring-2 focus:ring-green-200'
                  : isFocused
                    ? 'border-primary-blue bg-blue-50 focus:ring-2 focus:ring-blue-200'
                    : 'border-gray-300 bg-white hover:border-gray-400'}
          `}
          placeholder="••••••"
          aria-label={`6-digit verification code`}
          autoComplete="one-time-code"
          autoFocus
        />
      </div>

      {/* Progress Indicator */}
      <div className="flex justify-center">
        <div className="flex gap-1">
          {Array.from({ length: 6 }, (_, index) => (
            <div
              key={index}
              className={`w-2 h-2 rounded-full transition-all duration-200 ${
                value[index]
                  ? (error ? 'bg-red-500' : 'bg-green-500')
                  : (isFocused && index === value.length) ? 'bg-primary-blue' : 'bg-gray-300'
              }`}
            />
          ))}
        </div>
      </div>
    </div>
  );
};